/**
 * User-region detection for consent-banner gating.
 *
 * We use the Cloudflare trace endpoint (https://www.cloudflare.com/cdn-cgi/trace)
 * because it is:
 * - CORS-enabled for all origins,
 * - cookie-less (no identifier leaks across visits),
 * - stable (operated as piece of public internet infrastructure),
 * - low-latency (typically <100ms), and
 * - free (no API key, no rate-limit surprises for reasonable web traffic).
 *
 * The endpoint returns a plain-text body with a `loc=XX` line containing the
 * ISO 3166-1 alpha-2 country code for the request's originating IP.
 *
 * Behavior when detection fails (timeout, network error, malformed response):
 * the caller MUST fail-safe by treating the user as if they were in a
 * consent-required region. This keeps us on the safe side of GDPR/UK
 * GDPR/FADP/LGPD if our best-effort geolocation misses an EU user.
 */

import type { ConsentRequiredRegion } from "./consent";
import { CONSENT_REQUIRED_REGIONS, UNKNOWN_COUNTRY_SENTINELS } from "./consent";

const CLOUDFLARE_TRACE_URL = "https://www.cloudflare.com/cdn-cgi/trace";

/**
 * Trusted CDN/proxy country headers. Same set Composer reads for
 * ``get_edge_country_code_from_headers``, plus App Engine / Cloud Run.
 * Missing header means unknown country: fail closed (consent required).
 */
export const EDGE_COUNTRY_HEADER_CANDIDATES = [
  "cf-ipcountry",
  "x-vercel-ip-country",
  "cloudfront-viewer-country",
  "x-country-code",
  "x-appengine-country",
] as const;

export function normalizeCountryCode(
  value: string | null | undefined
): string | null {
  const normalized = value?.trim().toUpperCase() || "";
  if (!/^[A-Z]{2}$/.test(normalized)) return null;
  if ((UNKNOWN_COUNTRY_SENTINELS as readonly string[]).includes(normalized)) {
    return null;
  }
  return normalized;
}

/**
 * ISO 3166-1 alpha-2 country from trusted edge headers, or null when unknown.
 */
export function resolveCountryCodeFromRequestHeaders(
  headers: Pick<Headers, "get">
): string | null {
  for (const key of EDGE_COUNTRY_HEADER_CANDIDATES) {
    const code = normalizeCountryCode(headers.get(key));
    if (code) return code;
  }
  return null;
}

const DEFAULT_TIMEOUT_MS = 2000;
const SESSION_CACHE_KEY = "nrs_ga_region";

export interface RegionDetectionResult {
  /** ISO 3166-1 alpha-2 country code (uppercase) if detection succeeded. */
  country: string | null;
  /** True when the country lies in a jurisdiction requiring explicit consent. */
  consentRequired: boolean;
  /** True when detection failed; caller should treat as consentRequired=true. */
  failed: boolean;
}

export function isConsentRequiredForCountry(
  country: string | null | undefined
): boolean {
  if (!country) return true;
  const upper = country.toUpperCase();
  return (CONSENT_REQUIRED_REGIONS as readonly string[]).includes(upper);
}

function parseCloudflareTraceBody(body: string): string | null {
  const lines = body.split("\n");
  for (const line of lines) {
    const eq = line.indexOf("=");
    if (eq <= 0) continue;
    const key = line.slice(0, eq).trim().toLowerCase();
    if (key !== "loc") continue;
    const value = line.slice(eq + 1).trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(value)) return value;
    return null;
  }
  return null;
}

function readSessionCache(): RegionDetectionResult | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RegionDetectionResult>;
    if (
      typeof parsed.country !== "string" &&
      parsed.country !== null
    ) {
      return null;
    }
    if (
      typeof parsed.consentRequired !== "boolean" ||
      typeof parsed.failed !== "boolean"
    ) {
      return null;
    }
    return parsed as RegionDetectionResult;
  } catch {
    return null;
  }
}

function writeSessionCache(result: RegionDetectionResult): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(result));
  } catch {
    /* quota / privacy mode — non-fatal */
  }
}

async function fetchCloudflareTrace(
  timeoutMs: number,
  fetchImpl: typeof fetch
): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(CLOUDFLARE_TRACE_URL, {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    return await response.text();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Detects the user's country for consent-gating purposes. Caches the result
 * in sessionStorage so repeated calls within a tab session do not re-hit the
 * network.
 *
 * Always resolves (never throws). On any failure, returns a result with
 * `failed: true` and `consentRequired: true` so the caller can render the
 * banner without special-case error handling.
 */
export async function detectUserRegion(options?: {
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
  bypassCache?: boolean;
}): Promise<RegionDetectionResult> {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options?.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : null);

  if (!options?.bypassCache) {
    const cached = readSessionCache();
    if (cached) return cached;
  }

  if (!fetchImpl) {
    const result: RegionDetectionResult = {
      country: null,
      consentRequired: true,
      failed: true,
    };
    writeSessionCache(result);
    return result;
  }

  const body = await fetchCloudflareTrace(timeoutMs, fetchImpl);
  if (!body) {
    const result: RegionDetectionResult = {
      country: null,
      consentRequired: true,
      failed: true,
    };
    writeSessionCache(result);
    return result;
  }

  const country = parseCloudflareTraceBody(body);
  const result: RegionDetectionResult = {
    country,
    consentRequired: isConsentRequiredForCountry(country),
    failed: country === null,
  };
  writeSessionCache(result);
  return result;
}

export type { ConsentRequiredRegion };
